import {FC, useContext, useEffect, useMemo} from 'react';
import DateRangePicker from 'components/DateRangePicker';
import {DateRangePickerProps} from 'components/DateRangePicker/DateRangePicker';
import {useGraphqlQuery} from 'hooks/useGraphqlQuery';
import {GET_PUBLIC_HOLIDAYS} from 'graphql/absences/Querys';
import {authContext} from 'contextApi//context/auth';
import {parse} from 'date-fns';
import {Day} from 'models/day';
import {
  HOLIDAY,
  MONETIZATION,
  DAY_OFF,
  ADDITIONAL_LEAVE,
  LeaveRequestType,
} from 'models/leaveRequestType';

interface Holiday {
  name: string;
  date: string;
}

const HolidaysDatePickerController: FC<
  DateRangePickerProps & {leaveType?: string}
> = ({leaveType, ...other}) => {
  const DATE_FORMAT = 'yyyy-MM-dd';
  const {userDetails} = useContext(authContext);
  const countryCode = userDetails?.data?.country || '';

  const weekendsDisabled = useMemo((): boolean => {
    if (countryCode === 'BRA') {
      const allowsNaturalDays = [
        HOLIDAY,
        DAY_OFF,
        MONETIZATION,
        ADDITIONAL_LEAVE,
      ];
      return !allowsNaturalDays.includes(leaveType as LeaveRequestType);
    }

    return true;
  }, [countryCode, leaveType]);

  const {data: publicHolidays, fetchData: fetchPublicHolidays} =
    useGraphqlQuery<Day[], {countryCode: string; year: number}>(
      [],
      GET_PUBLIC_HOLIDAYS,
      'publicHolidays'
    );

  const getHolidays = async (year: number) => {
    if (!countryCode) {
      return;
    }

    await fetchPublicHolidays(
      {
        countryCode,
        year: year,
      },
      data => {
        return data.map((item: Holiday) => ({
          name: item.name,
          date: parse(item.date, DATE_FORMAT, new Date()),
        }));
      }
    );
  };

  const onChangeYear = async (year: number) => {
    const exists = publicHolidays.find(
      item => item.date.getFullYear() === year
    );
    if (!exists) {
      await getHolidays(year);
    }
  };

  useEffect(() => {
    getHolidays(new Date().getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  return (
    <DateRangePicker
      {...other}
      weekendsDisabled={weekendsDisabled}
      holidays={publicHolidays.map(day => day.date)}
      onChangeYear={onChangeYear}
    />
  );
};

export default HolidaysDatePickerController;
